// Grocery list page. Items are grouped by category. Starred-low-stock items
// sort first within each group, and a top banner offers a one-tap "add all
// priority items" shortcut. Tapping an item opens the check-off modal which
// adds it to the pantry (with optional substitute scan) and marks complete.

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import { CheckoffModal } from "../components/CheckoffModal";
import type {
  GroceryEntry,
  InventoryEntry,
  LowStockProduct,
} from "../types";

// ---------- helpers ----------

interface CategoryGroup {
  sortKey: string;
  label: string;
  items: GroceryEntry[];
}

function groupByCategory(items: GroceryEntry[]): CategoryGroup[] {
  const buckets = new Map<string, CategoryGroup>();
  for (const it of items) {
    const cat = (it.effective_category ?? "").trim();
    const sortKey = cat ? `1-${cat.toLowerCase()}` : "9-none";
    const label = cat || "Uncategorized";
    const existing = buckets.get(sortKey);
    if (existing) existing.items.push(it);
    else buckets.set(sortKey, { sortKey, label, items: [it] });
  }
  // Sort items within each group: starred-low-stock first, then alphabetical.
  for (const g of buckets.values()) {
    g.items.sort((a, b) => {
      const aStar = a.product?.is_starred ? 1 : 0;
      const bStar = b.product?.is_starred ? 1 : 0;
      if (aStar !== bStar) return bStar - aStar;
      return a.name.localeCompare(b.name);
    });
  }
  return [...buckets.values()].sort((a, b) =>
    a.sortKey.localeCompare(b.sortKey)
  );
}

// ---------- main component ----------

export function GroceryList() {
  const [items, setItems] = useState<GroceryEntry[]>([]);
  const [completed, setCompleted] = useState<GroceryEntry[]>([]);
  const [lowStock, setLowStock] = useState<LowStockProduct[]>([]);
  const [inventory, setInventory] = useState<InventoryEntry[]>([]);
  const [suggestions, setSuggestions] = useState<{
    locations: string[];
    categories: string[];
  }>({ locations: [], categories: [] });
  const [showCompleted, setShowCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Add-form state.
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState("1");
  const [newUnit, setNewUnit] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [adding, setAdding] = useState(false);

  // Check-off modal state.
  const [checkoffItem, setCheckoffItem] = useState<GroceryEntry | null>(null);

  // Inline-edit state. Only one row open at a time.
  const [editingId, setEditingId] = useState<string | null>(null);

  async function loadAll() {
    setLoading(true);
    setErr(null);
    try {
      const [g, ls, sug, inv] = await Promise.all([
        api.listGrocery(true),
        api.listLowStock(),
        api.getSuggestions(),
        api.listInventory().catch(() => ({ items: [] as InventoryEntry[] })),
      ]);
      const active = g.items.filter((i) => i.completed_at == null);
      const done = g.items.filter((i) => i.completed_at != null);
      setItems(active);
      setCompleted(done);
      setLowStock(ls.items);
      setSuggestions(sug);
      setInventory(inv.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  // Pantry inventory aggregated by lowercased name → total quantity. Used to
  // power the "📦 you already have N of these" hint on the add form.
  const inventoryByName = useMemo(() => {
    const m = new Map<string, { qty: number; productName: string }>();
    for (const e of inventory) {
      const k = e.product.name.toLowerCase().trim();
      const cur = m.get(k);
      if (cur) cur.qty += e.quantity;
      else m.set(k, { qty: e.quantity, productName: e.product.name });
    }
    return m;
  }, [inventory]);

  const matchedInventory = useMemo(() => {
    const k = newName.trim().toLowerCase();
    if (k.length < 2) return null;
    return inventoryByName.get(k) ?? null;
  }, [newName, inventoryByName]);

  // Suggestions for the autocomplete on the new-item field. Combines pantry
  // product names with previously-bought (completed) grocery item names.
  const nameSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const v of inventoryByName.values()) set.add(v.productName);
    for (const c of completed) set.add(c.name);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [inventoryByName, completed]);

  // Starred products that are below their threshold AND not already on the
  // active grocery list.
  const priorityRestocks = useMemo(() => {
    const onListProductIds = new Set(
      items.map((i) => i.product_id).filter((x): x is string => !!x)
    );
    return lowStock.filter(
      (p) => p.is_starred && !onListProductIds.has(p.id)
    );
  }, [lowStock, items]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.addGroceryItem({
        name: newName.trim(),
        quantity: parseFloat(newQty) || 1,
        unit: newUnit.trim() || null,
        category: newCategory.trim() || null,
      });
      setNewName("");
      setNewQty("1");
      setNewUnit("");
      setNewCategory("");
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add");
    } finally {
      setAdding(false);
    }
  }

  async function addAllPriority() {
    try {
      for (const p of priorityRestocks) {
        await api.addGroceryItem({ product_id: p.id, name: p.name });
      }
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add priority items");
    }
  }

  async function removeItem(id: string) {
    try {
      await api.deleteGroceryItem(id);
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to remove");
    }
  }

  async function uncompleteItem(id: string) {
    try {
      await api.updateGroceryItem(id, { completed: false });
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    }
  }

  async function saveEdit(
    id: string,
    patch: {
      name: string;
      quantity: number;
      unit: string | null;
      category: string | null;
    }
  ) {
    try {
      await api.updateGroceryItem(id, patch);
      setEditingId(null);
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to save");
    }
  }

  const groups = useMemo(() => groupByCategory(items), [items]);

  return (
    <div className="mx-auto max-w-2xl p-4">
      {/* Add form */}
      <form
        onSubmit={handleAdd}
        className="card mb-4 space-y-2 p-3"
        aria-label="Add to grocery list"
      >
        <div>
          <input
            className="input"
            list="grocery-name-suggestions"
            placeholder="Add an item…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <datalist id="grocery-name-suggestions">
            {nameSuggestions.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          {matchedInventory && (
            <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              📦 You already have {matchedInventory.qty}{" "}
              {matchedInventory.qty === 1 ? "of this" : "of these"} in your pantry.
            </p>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input
            type="number"
            min="0"
            step="any"
            className="input"
            placeholder="Qty"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value)}
          />
          <input
            className="input"
            placeholder="Unit"
            value={newUnit}
            onChange={(e) => setNewUnit(e.target.value)}
          />
          <input
            className="input"
            list="grocery-category-suggestions"
            placeholder="Category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
          />
          <datalist id="grocery-category-suggestions">
            {suggestions.categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <button
          type="submit"
          className="btn-primary w-full"
          disabled={adding || !newName.trim()}
        >
          {adding ? "Adding…" : "Add to list"}
        </button>
      </form>

      {/* Priority restock banner */}
      {priorityRestocks.length > 0 && (
        <div className="card mb-4 border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30">
          <div className="flex items-start gap-3">
            <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" fill="currentColor">
              <path d="M12 2l2.39 4.84L20 7.62l-3.79 3.7.89 5.21L12 14.27l-5.1 2.26.89-5.21L4 7.62l5.61-.78L12 2z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                {priorityRestocks.length}{" "}
                {priorityRestocks.length === 1 ? "priority item needs" : "priority items need"} restocking
              </p>
              <p className="mt-0.5 text-xs text-red-800 dark:text-red-300">
                {priorityRestocks.map((p) => p.name).join(", ")}
              </p>
              <button
                onClick={addAllPriority}
                className="mt-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
              >
                Add all to list
              </button>
            </div>
          </div>
        </div>
      )}

      {err && (
        <div className="card mb-4 border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          {err}
        </div>
      )}

      {/* Active list */}
      {loading ? (
        <div className="py-12 text-center text-stone-500 dark:text-stone-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 px-6 py-8 text-center">
          <div className="text-3xl">🛒</div>
          <p className="font-medium">List is empty</p>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            Add an item above, or use the "+ list" buttons on inventory items.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.sortKey}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-700 dark:text-stone-300">
                <span>{g.label}</span>
                <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-normal text-stone-500 dark:bg-stone-800 dark:text-stone-400">
                  {g.items.length}
                </span>
              </h2>
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <ListRow
                    key={it.id}
                    item={it}
                    editing={editingId === it.id}
                    categorySuggestions={suggestions.categories}
                    onCheckOff={() => setCheckoffItem(it)}
                    onDelete={() => removeItem(it.id)}
                    onStartEdit={() => setEditingId(it.id)}
                    onCancelEdit={() => setEditingId(null)}
                    onSaveEdit={(patch) => saveEdit(it.id, patch)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {/* Completed section */}
      {completed.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            className="text-sm text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
          >
            {showCompleted ? "Hide" : "Show"} completed ({completed.length})
          </button>
          {showCompleted && (
            <ul className="mt-2 space-y-1.5">
              {completed.map((it) => (
                <li
                  key={it.id}
                  className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-500 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-400"
                >
                  <span className="line-through">{it.name}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => uncompleteItem(it.id)}
                      className="text-xs hover:text-stone-800 dark:hover:text-stone-200"
                      aria-label="Move back to list"
                    >
                      Undo
                    </button>
                    <button
                      onClick={() => removeItem(it.id)}
                      className="ml-2 text-xs hover:text-red-600 dark:hover:text-red-400"
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <CheckoffModal
        open={!!checkoffItem}
        item={checkoffItem}
        suggestions={suggestions}
        onClose={() => setCheckoffItem(null)}
        onDone={async () => {
          setCheckoffItem(null);
          await loadAll();
        }}
      />
    </div>
  );
}

// ---------- subcomponents ----------

interface EditPatch {
  name: string;
  quantity: number;
  unit: string | null;
  category: string | null;
}

function ListRow({
  item,
  editing,
  categorySuggestions,
  onCheckOff,
  onDelete,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
}: {
  item: GroceryEntry;
  editing: boolean;
  categorySuggestions: string[];
  onCheckOff: () => void;
  onDelete: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (patch: EditPatch) => void;
}) {
  if (editing) {
    return (
      <EditRow
        item={item}
        categorySuggestions={categorySuggestions}
        onCancel={onCancelEdit}
        onSave={onSaveEdit}
      />
    );
  }

  const starred = !!item.product?.is_starred;
  return (
    <li className="card flex items-center gap-3 p-3">
      <button
        type="button"
        onClick={onCheckOff}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 border-stone-300 hover:border-brand-500 hover:bg-brand-50 dark:border-stone-600 dark:hover:bg-brand-900/30"
        aria-label={`Mark ${item.name} as bought`}
      >
        <span className="sr-only">Mark bought</span>
      </button>

      {item.product?.image_url ? (
        <img
          src={item.product.image_url}
          alt=""
          className="h-10 w-10 shrink-0 rounded-md object-cover"
        />
      ) : null}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {starred && (
            <span
              className="text-amber-500 dark:text-amber-400"
              title="Essential / starred item"
              aria-label="Starred"
            >
              ★
            </span>
          )}
          <p className="truncate font-medium">{item.name}</p>
        </div>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {item.quantity}
          {item.unit ? ` ${item.unit}` : ""}
          {starred && (
            <span className="ml-2 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Restock priority
            </span>
          )}
        </p>
      </div>

      <button
        type="button"
        onClick={onStartEdit}
        className="rounded-md p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-700 dark:text-stone-500 dark:hover:bg-stone-800 dark:hover:text-stone-200"
        aria-label={`Edit ${item.name}`}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <button
        type="button"
        onClick={onDelete}
        className="rounded-md p-2 text-stone-400 hover:bg-stone-100 hover:text-red-600 dark:text-stone-500 dark:hover:bg-stone-800 dark:hover:text-red-400"
        aria-label={`Remove ${item.name}`}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" />
        </svg>
      </button>
    </li>
  );
}

function EditRow({
  item,
  categorySuggestions,
  onCancel,
  onSave,
}: {
  item: GroceryEntry;
  categorySuggestions: string[];
  onCancel: () => void;
  onSave: (patch: EditPatch) => void;
}) {
  // Pre-fill category from the row's effective_category so users see what the
  // grouping is using, not the (often null) raw grocery_items.category column.
  const [name, setName] = useState(item.name);
  const [qty, setQty] = useState(String(item.quantity));
  const [unit, setUnit] = useState(item.unit ?? "");
  const [category, setCategory] = useState(item.effective_category ?? "");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      quantity: parseFloat(qty) || 1,
      unit: unit.trim() || null,
      category: category.trim() || null,
    });
  }

  return (
    <li className="card space-y-2 p-3">
      <form onSubmit={handleSubmit} className="space-y-2" aria-label={`Edit ${item.name}`}>
        <input
          className="input"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
        <div className="grid grid-cols-3 gap-2">
          <input
            type="number"
            min="0"
            step="any"
            className="input"
            placeholder="Qty"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <input
            className="input"
            placeholder="Unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
          <input
            className="input"
            list={`grocery-edit-categories-${item.id}`}
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <datalist id={`grocery-edit-categories-${item.id}`}>
            {categorySuggestions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary flex-1" disabled={!name.trim()}>
            Save
          </button>
          <button type="button" onClick={onCancel} className="btn-secondary flex-1">
            Cancel
          </button>
        </div>
      </form>
    </li>
  );
}
